import { Container, Box, Typography, Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { Component } from 'react';
import { reportExpected } from 'services/observability';

/**
 * A second render error within this window, after the automatic retry, is
 * treated as persistent: the fallback is shown instead of retrying again.
 */
export const RENDER_ERROR_RETRY_WINDOW_MS = 10_000;

const CHUNK_RELOAD_KEY = 'chunkErrorReloaded';

/**
 * Minimum time between two automatic reloads. It stops a reload loop when the
 * chunk is missing for good, yet lets the next deploy in the same tab reload
 * again: the old guard was a flag kept for the whole session, so only the
 * first stale bundle of a session ever reloaded by itself.
 */
export const CHUNK_RELOAD_MIN_INTERVAL_MS = 60_000;

/**
 * If the page is still here this long after asking for a reload, the reload
 * did not happen: show the "platform updated" fallback instead of a blank page.
 */
export const STALE_CHUNK_RELOAD_GRACE_MS = 10_000;

/**
 * Set once a reload for a stale chunk has been requested. Until the browser
 * actually navigates away, anything that breaks is a consequence of the
 * missing chunk: nothing to retry, nothing to report. Notably React #306
 * ("Lazy element type must resolve to a class or function. Received a promise
 * that resolves to: undefined"): when the vite:preloadError handler prevents
 * the error, Vite resolves the failed import to undefined instead of throwing,
 * and React renders that in the moment before the reload -- that is what
 * reached Sentry on 29/09 from prod, mdp and ss.
 */
let staleChunkReloadRequested = false;

export const isStaleChunkReloadRequested = () => staleChunkReloadRequested;

/** Tests only: the flag lives for the page, and a test is not a page. */
export const resetStaleChunkReloadForTests = () => {
  staleChunkReloadRequested = false;
};

/**
 * Reloads the page to pick up the current deploy, unless it already did so in
 * the last {@link CHUNK_RELOAD_MIN_INTERVAL_MS}. Returns whether it reloaded.
 */
export const reloadForStaleChunk = (): boolean => {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    // Storage blocked: fall through and reload, the interval is best effort.
  }
  if (Date.now() - last < CHUNK_RELOAD_MIN_INTERVAL_MS) return false;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Without storage a missing chunk could reload forever: show the fallback.
    return false;
  }
  staleChunkReloadRequested = true;
  reportExpected('stale_chunk_reload', { beforeUnload: true });
  window.location.reload();
  return true;
};

const CHUNK_ERROR_MESSAGES = [
  /Failed to fetch dynamically imported module/, // Chrome, Edge
  /error loading dynamically imported module/, // Firefox
  /Importing a module script failed/, // Safari
  /Unable to preload CSS/, // Vite CSS preload
];

type Props = {
  children?: React.ReactNode;
  /**
   * Runs before the one automatic retry after a render error, while the
   * subtree is unmounted. The app root passes a clear of the Apollo store.
   */
  onRetry?: () => unknown;
};

type State = {
  /** Fallback UI shown: `chunk` after a stale deploy, `render` otherwise. */
  fallback: 'chunk' | 'render' | null;
  /** A render error was caught and the retry has not run yet. */
  recovering: boolean;
  /** Bumped on every retry so the subtree mounts from scratch. */
  attempt: number;
};

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { fallback: null, recovering: false, attempt: 0 };

  private lastRetryAt = 0;
  private unmounted = false;

  static isChunkError(error: any): boolean {
    if (!error) return false;
    if (error.name === 'ChunkLoadError') return true;
    // SyntaxError: Unexpected token '<' — server returned HTML (index.html) instead of a JS chunk
    if (
      error instanceof SyntaxError &&
      error.message.includes("Unexpected token '<'")
    )
      return true;
    // Vite: a lazy chunk of the previous deploy is gone. The server answers the
    // missing file with index.html, and each browser words the failure of
    // import() differently. These used to fall through as ordinary render
    // errors, so since the move from webpack to Vite a deploy blanked the page
    // of anyone who navigated to a lazy module with the old bundle loaded.
    return CHUNK_ERROR_MESSAGES.some((re) => re.test(String(error.message)));
  }

  static getDerivedStateFromError(error: any): Partial<State> | null {
    // A reload is on its way: render nothing until the browser navigates.
    if (staleChunkReloadRequested) return { recovering: true };
    if (ErrorBoundary.isChunkError(error)) {
      // Auto-reload to recover from a stale bundle after a deploy.
      if (reloadForStaleChunk()) return { recovering: true };
      return { fallback: 'chunk' };
    }
    // Any other render error: keep the subtree unmounted until
    // componentDidCatch decides between one retry and the fallback.
    //
    // This used to return nothing, so React re-rendered the children straight
    // away. That only ever "worked" because the Apollo provider below built a
    // brand new client -- empty cache, new WebSocket -- on every remount: an
    // error that depends on the data turned into an endless remount loop, one
    // leaked socket per round (learners on ss held 110-156 at once). With one
    // client per page the same error would now escalate to the root and blank
    // the whole page instead.
    return { recovering: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.log('Error Boundary Caught:', error, errorInfo);
    if (staleChunkReloadRequested) {
      this.showChunkFallbackIfStillHere();
      return;
    }
    if (ErrorBoundary.isChunkError(error)) return;

    Sentry.captureException(error, {
      tags: { area: 'error-boundary' },
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });

    const now = Date.now();
    if (now - this.lastRetryAt < RENDER_ERROR_RETRY_WINDOW_MS) {
      this.setState({ fallback: 'render', recovering: false });
      return;
    }
    this.lastRetryAt = now;
    this.retry();
  }

  // The visitor is now looking at a fallback page: count it, whatever led
  // there. On mount too: an error in the very first render shows the fallback
  // without any update, which is the usual case at the app root.
  componentDidMount() {
    this.countFallback(null);
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    this.countFallback(prevState.fallback);
  }

  private countFallback(previous: State['fallback']) {
    const { fallback } = this.state;
    if (fallback && fallback !== previous) {
      reportExpected(
        fallback === 'chunk' ? 'stale_chunk_fallback' : 'render_error_fallback',
      );
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.reloadGraceTimer) clearTimeout(this.reloadGraceTimer);
  }

  private reloadGraceTimer: ReturnType<typeof setTimeout> | undefined;

  private showChunkFallbackIfStillHere() {
    if (this.reloadGraceTimer) return;
    this.reloadGraceTimer = setTimeout(() => {
      if (!this.unmounted)
        this.setState({ fallback: 'chunk', recovering: false });
    }, STALE_CHUNK_RELOAD_GRACE_MS);
  }

  /**
   * One retry, after `onRetry` (at the app root: an empty Apollo cache, which is
   * what the old per-render client gave implicitly, now without opening another
   * socket). The subtree is unmounted while this runs, so no query is active.
   */
  private async retry() {
    try {
      await this.props.onRetry?.();
    } catch (e) {
      console.warn('ErrorBoundary: onRetry failed', e);
    }
    if (this.unmounted) return;
    this.setState((s) => ({ recovering: false, attempt: s.attempt + 1 }));
  }

  reload = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // ignore
    }
    window.location.reload();
  };

  render() {
    const { fallback, recovering, attempt } = this.state;
    if (fallback) {
      return (
        <Container maxWidth="sm" sx={{ height: '100vh' }}>
          <Box
            display="flex"
            height={'100%'}
            flexDirection="column"
            justifyContent={'center'}
            alignItems="center"
          >
            <Typography align="center" variant="h5">
              {fallback === 'chunk'
                ? 'The platform has been updated, please refresh your browser to see the latest content.'
                : 'Something went wrong. Please refresh the page.'}
            </Typography>
            <Button
              sx={{ marginTop: 5 }}
              variant="outlined"
              onClick={this.reload}
            >
              {'Refresh'}
            </Button>
          </Box>
        </Container>
      );
    }
    if (recovering) return null;
    return <React.Fragment key={attempt}>{this.props.children}</React.Fragment>;
  }
}
